import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue } from "framer-motion";
import { ArrowRight, Zap, Users, Trophy, Clock } from "lucide-react";
import { useLang } from "@/i18n";

const HERO_IMG = "https://images.unsplash.com/photo-1605296867304-46d5465a13f1?crop=entropy&cs=srgb&fm=jpg&q=85";
const HERO_BG = "https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?crop=entropy&cs=srgb&fm=jpg&q=85";

const Hero = ({ onEnroll }) => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 700], [0, 160]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useTransform(my, [-0.5, 0.5], [5, -5]);
  const rotateY = useTransform(mx, [-0.5, 0.5], [-5, 5]);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const stats = [
    { icon: Users, value: "4,500+", label: t.stat_members, testid: "stat-members" },
    { icon: Trophy, value: "15+", label: t.stat_trainers, testid: "stat-trainers" },
    { icon: Clock, value: "17h", label: t.stat_open, testid: "stat-open" },
    { icon: Zap, value: "120+", label: t.stat_machines, testid: "stat-machines" },
  ];

  return (
    <section id="top" ref={ref} onMouseMove={onMouseMove} className="relative overflow-hidden pt-28 sm:pt-32" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img src={HERO_BG} alt="" className="h-[115%] w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/60 to-ink" />
      </motion.div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-16 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:pb-24">
        <div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-volt/30 bg-volt/10 px-4 py-1.5"
            data-testid="hero-badge"
          >
            <span className="h-2 w-2 animate-pulse rounded-full bg-volt" />
            <span className="font-mono text-xs font-semibold uppercase tracking-widest text-volt">{t.hero_badge}</span>
          </motion.div>

          <h1 className="font-display text-6xl font-extrabold uppercase leading-[0.9] tracking-tight text-white sm:text-7xl lg:text-[6.5rem]" data-testid="hero-title">
            {t.hero_lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.35 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                  className={`block ${i === 1 ? "text-volt" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-6 max-w-md text-base text-zinc-400 leading-relaxed sm:text-lg"
            data-testid="hero-subtitle"
          >
            {t.hero_sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-enroll-button"
              onClick={() => onEnroll(null)}
              className="group flex items-center gap-2.5 rounded-full bg-volt px-8 py-4 font-display text-base font-bold uppercase tracking-widest text-ink volt-glow transition-all duration-200 hover:bg-volt-hover hover:scale-[1.04]"
            >
              {t.hero_cta_primary}
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
            </button>
            <a
              href="#packages"
              onClick={(e) => { e.preventDefault(); document.querySelector("#packages")?.scrollIntoView({ behavior: "smooth" }); }}
              data-testid="hero-packages-button"
              className="rounded-full border border-zinc-700 px-8 py-4 font-display text-base font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-200 hover:border-volt hover:text-volt"
            >
              {t.hero_cta_secondary}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative hidden lg:block"
          style={{ perspective: 1000 }}
        >
          <div className="absolute -left-4 -top-4 h-full w-full rounded-2xl border-2 border-volt/40" />
          <motion.div style={{ rotateX, rotateY }} className="relative">
            <img
              src={HERO_IMG}
              alt="Athlete training at Fitnastic"
              data-testid="hero-image"
              className="relative aspect-[4/5] w-full rounded-2xl object-cover"
              style={{ clipPath: "polygon(0 0, 100% 4%, 100% 100%, 0 96%)" }}
            />
            <div className="absolute bottom-6 left-6 rounded-xl border border-zinc-700/60 bg-ink/80 px-5 py-3 backdrop-blur-md" data-testid="hero-floating-card">
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">SINCE 2015 — WEST BENGAL</p>
              <p className="font-display text-xl font-bold uppercase tracking-wider text-white">
                No shortcuts. <span className="text-volt">Only reps.</span>
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <div className="mx-auto max-w-7xl px-5 pb-20 sm:px-8">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="hero-stats">
          {stats.map((s, i) => (
            <motion.div
              key={s.testid}
              data-testid={s.testid}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="card-scan rounded-xl border border-zinc-800 bg-surface/70 p-5"
            >
              <s.icon className="mb-3 h-5 w-5 text-volt" />
              <p className="font-display text-3xl font-extrabold text-white sm:text-4xl">{s.value}</p>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-zinc-500">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
